"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "../../lib/utils";

// Radix throws if a Tooltip has no Provider above it, so `Tooltip` has always
// supplied one itself. The cost was that the supplied provider became the
// *nearest* one, silently overriding every `<TooltipProvider delayDuration={n}>`
// a caller had wrapped around a group. This context lets `Tooltip` see whether a
// real provider is already above it and stand down when one is, while still
// being safe to render anywhere on its own.
const HasTooltipProvider = React.createContext(false);

function TooltipProvider({
  // A tooltip that opens the instant the cursor crosses it fires on every
  // incidental pass. The delay is the filter; `skipDelayDuration` is what stops
  // the filter from taxing the second and third tooltip in a row, and it only
  // works across siblings that share one provider, which is what the context
  // above exists to allow.
  delayDuration = 300,
  skipDelayDuration = 300,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <HasTooltipProvider.Provider value={true}>
      <TooltipPrimitive.Provider
        data-slot="tooltip-provider"
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
        {...props}
      >
        {children}
      </TooltipPrimitive.Provider>
    </HasTooltipProvider.Provider>
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const hasProvider = React.useContext(HasTooltipProvider);
  const root = <TooltipPrimitive.Root data-slot="tooltip" {...props} />;

  return hasProvider ? root : <TooltipProvider>{root}</TooltipProvider>;
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  side,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const showArrow = side !== "left" && side !== "right";

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        side={side}
        className={cn(
          "bg-primary text-primary-foreground data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 z-[70] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance shadow-md duration-125 ease-out data-[state=closed]:duration-100",
          className,
        )}
        {...props}
      >
        {children}
        {showArrow && (
          <TooltipPrimitive.Arrow className="bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
